import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { Button, Flex } from 'antd';
import { FC, memo, useLayoutEffect, useMemo, useRef } from 'react';
import { TextEllipsis } from '../../common/components';
import {
  DetailsTableRowButton,
  getValidPeriodDisplayValueWithFormValueAttached,
} from '../../common/form';
import { Table } from '../../common/tanstacktable';
import {
  suppressHref,
  useHrefWithQueryParams,
} from '../../common/useHrefWithQueryParams';
import { usePanelParams } from '../usePanelParams';
import { sortPeriod } from '../../common/utils';
import { SystemClassifierOption } from '../../configuration';
import { useDataObjectContext } from '../DataObjectContext';
import { RequiredKeyDataType } from '../schema';
import translations from '../translations/KeysViewTable.translations.json';
import { useTableFilter } from '../useTableFilter';
import { KeysViewPanel } from './KeysViewPanel';
import { RelatedAlgorithmsViewModal } from './RelatedAlgorithmsViewModal';
import { useKeyOptions } from './useKeyOptions';
import {
  KeyTableWrapper,
  renderClassifierPreview,
  renderClassifiersPreview,
} from './utils';
import styled from 'styled-components';
import { colorLink } from '@atlas-frontend/design-tokens';

const { addTranslations, useTranslation } = i18nNsFactory('keys-view-table');
addTranslations(translations);

const columnHelper = createColumnHelper<RequiredKeyDataType>();

export const KeysViewTable = ({
  data,
  indicatorStatus,
}: {
  data: RequiredKeyDataType[];
  indicatorStatus?: boolean;
}) => {
  const { t } = useTranslation();
  const { options, recipientClassifiers, ruleClassifiers, statusClassifiers } =
    useKeyOptions(indicatorStatus);
  const { setRequiredKeyFilters, setValidateKeysModalOpen } =
    useDataObjectContext();
  const { columnFilters, setColumnFilters } = useTableFilter(
    setRequiredKeyFilters,
  );
  const columns = useMemo(
    () => [
      columnHelper.accessor('sequenceNumber', {
        header: t('column.sequenceNumber.label'),
        meta: {
          width: 75,
          className: 'sticky-left',
          textAlign: 'right',
        },
        filterFn: 'includesString',
        sortingFn: 'basic',
      }),
      columnHelper.accessor('key', {
        header: t('column.key.label'),
        cell: KeyCell,
        meta: {
          minWidth: 300,
          width: 520,
        },
      }),
      columnHelper.accessor('name', {
        header: t('column.name.label'),
        meta: {
          minWidth: 300,
          width: 520,
        },
      }),
      ...(indicatorStatus
        ? [
            columnHelper.accessor('statusId', {
              header: t('column.statusId.label'),
              cell: (props) => (
                <ClassifierCell
                  {...props}
                  value={props.row.original.statusId}
                  options={statusClassifiers}
                />
              ),
              filterFn: 'includesOption',
              meta: {
                minWidth: 90,
                width: 90,
              },
            }),
          ]
        : []),
      columnHelper.accessor('ruleTypeId', {
        header: t('column.ruleTypeId.label'),
        cell: (props) => (
          <ClassifierCell
            {...props}
            value={props.row.original.ruleTypeId}
            options={ruleClassifiers}
          />
        ),
        filterFn: 'includesOption',
        meta: {
          width: 200,
        },
      }),
      columnHelper.accessor(
        (rowData) =>
          getValidPeriodDisplayValueWithFormValueAttached(rowData.validPeriod),
        {
          id: 'validPeriod',
          header: t('column.validPeriod.label'),
          meta: { width: 140 },
          sortingFn: (a, b) =>
            sortPeriod(a.original.validPeriod, b.original.validPeriod),
          filterFn: 'validPeriod',
        },
      ),
      columnHelper.accessor('voluntary', {
        header: t('column.voluntary.label'),
        cell: BooleanCell,
        filterFn: 'includesOption',
        meta: {
          minWidth: 112,
          width: 112,
        },
      }),
      columnHelper.accessor('recipientIds', {
        header: t('column.recipientIds.label'),
        cell: (props) => (
          <ClassifierCell
            {...props}
            multiple
            value={props.row.original.recipientIds}
            options={recipientClassifiers}
          />
        ),
        filterFn: 'includesOption',
        meta: {
          width: 200,
        },
      }),
      columnHelper.accessor('description', {
        header: t('column.description.label'),
        enableSorting: false,
        meta: {
          width: 150,
        },
      }),
      columnHelper.display({
        id: 'operations',
        enableSorting: false,
        enableColumnFilter: false,
        cell: OperationsCell,
        meta: {
          className: 'sticky-right',
          width: 60,
        },
      }),
    ],
    [
      indicatorStatus,
      recipientClassifiers,
      ruleClassifiers,
      statusClassifiers,
      t,
      data,
    ],
  );

  const [
    { requiredKeyId, requiredKeyIndex, requiredKeyAlgorithmsIndex },
    setPanelParams,
  ] = usePanelParams();
  const viewableRequiredKey = useMemo(
    () =>
      requiredKeyId
        ? data.find(({ id }) => id === Number(requiredKeyId))
        : data[Number(requiredKeyIndex)],
    [data, requiredKeyId, requiredKeyIndex],
  );

  const viewableRelatedAlgorithmsRequiredKey =
    data[Number(requiredKeyAlgorithmsIndex)];

  return (
    <KeyTableWrapper vertical align="end" gap="middle">
      <Table
        name="requiredKeys"
        data={data}
        columns={columns}
        filters={columnFilters}
        onFilter={setColumnFilters}
        defaultSort={[{ id: 'sequenceNumber', desc: false }]}
        options={options}
        rowKey="code"
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
        actions={
          data.length > 0 ? (
            <Button
              id="bulk-check-keys"
              onClick={() => setValidateKeysModalOpen(true)}
            >
              {t('button.check-keys')}
            </Button>
          ) : undefined
        }
      />
      {viewableRequiredKey && (
        <KeysViewPanel
          requiredKey={viewableRequiredKey}
          indicatorStatus={indicatorStatus}
          onClose={setPanelParams}
        />
      )}
      {viewableRelatedAlgorithmsRequiredKey && (
        <RelatedAlgorithmsViewModal
          requiredKey={viewableRelatedAlgorithmsRequiredKey}
          onClose={setPanelParams}
          isEditMode={false}
        />
      )}
    </KeyTableWrapper>
  );
};

const KeyCell: FC<CellContext<RequiredKeyDataType, any>> = memo((props) => {
  const [, setPanelParams] = usePanelParams();
  const { focusedKeyId, setFocusedKeyId } = useDataObjectContext();
  const { id: requiredKeyId } = props.row.original;
  const requiredKeyIndex = props.row.index;
  const params = requiredKeyId ? { requiredKeyId } : { requiredKeyIndex };
  const href = useHrefWithQueryParams(params);
  const shouldFocus =
    focusedKeyId !== undefined && focusedKeyId === String(requiredKeyId);
  const linkRef = useRef<HTMLAnchorElement | null>(null);

  useLayoutEffect(() => {
    if (shouldFocus && linkRef.current) {
      const timeoutId = setTimeout(() => {
        if (linkRef.current) {
          setTimeout(() => {
            linkRef?.current?.focus({ preventScroll: false });
            linkRef?.current?.setAttribute('data-custom-focus', 'true');
            setFocusedKeyId?.(undefined);
          }, 100);
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    } else linkRef?.current?.removeAttribute('data-custom-focus');
  }, [shouldFocus, setFocusedKeyId]);

  return (
    <KeyLink
      id={`view-key-${props.row.index}`}
      href={href}
      ref={linkRef}
      tabIndex={0}
      onClick={suppressHref(() => setPanelParams(params))}
    >
      <TextEllipsis>{props.renderValue()}</TextEllipsis>
    </KeyLink>
  );
});

const ClassifierCell: FC<
  | (CellContext<RequiredKeyDataType, any> & {
      multiple?: never;
      value: number | null;
      options: SystemClassifierOption[];
    })
  | {
      multiple: true;
      value: number[];
      options: SystemClassifierOption[];
    }
> = memo((props) => (
  <TextEllipsis>
    {props.multiple
      ? renderClassifiersPreview(props.value, props.options)
      : renderClassifierPreview(props.value ?? undefined, props.options)}
  </TextEllipsis>
));

const BooleanCell: FC<CellContext<RequiredKeyDataType, any>> = memo((props) => {
  const { t } = useTranslation();
  return (
    <>
      {props.renderValue() ? t('column.boolean.yes') : t('column.boolean.no')}
    </>
  );
});

const OperationsCell: FC<CellContext<RequiredKeyDataType, any>> = memo(
  (props) => {
    const { t } = useTranslation();
    const [, setPanelParams] = usePanelParams();
    const params = { requiredKeyAlgorithmsIndex: props.row.index };
    const href = useHrefWithQueryParams(params);
    return (
      <Flex justify="center" gap="small">
        <DetailsTableRowButton
          detailsTitle={t('button.details')}
          emptyTitle={t('button.no-details')}
          context={props}
          hasDetails={props.row.original.relatedAlgorithmIds.length > 0}
          href={href}
          onClick={suppressHref(() => setPanelParams(params))}
        />
      </Flex>
    );
  },
);

export const KeyLink = styled.a<{ color?: string }>`
  color: ${({ color }) => color || colorLink} !important;
  display: inline-block;
  position: relative;

  &&:focus,
  &&:focus-visible,
  &&[data-custom-focus='true'] {
    outline: 2px solid var(--ant-primary-color) !important;
    outline-offset: 2px;
    border-radius: 6px;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.35) !important;
  }
`;
